/**
 * SSEFrameParser - Stateful SSE frame reassembler
 *
 * AWS SDK delivers raw byte chunks that do NOT align with SSE frame boundaries.
 * A single chunk may contain partial frames, multiple frames, or frames split
 * across boundaries. This parser buffers input and reassembles complete frames.
 *
 * SSE frame format: `data: <json>\n\n`
 *
 * @example
 * const parser = new SSEFrameParser();
 * const events = parser.feed('data: {"type":"thinking"}\n\n');
 * // → [{type: "thinking"}]
 */
class SSEFrameParser {
  _buffer = '';


  /**
   * Feed raw string data into the parser.
   * Returns an array of parsed SSE event objects for any complete frames found.
   *
   * @param {string} rawString - Raw string chunk from the stream
   * @returns {Array<Object>} Parsed event objects from complete frames
   */
  feed(rawString) {
    if (!rawString) return [];

    this._buffer += rawString;
    const events = [];

    // Split on double newline (SSE frame boundary)
    let boundaryIndex;
    while ((boundaryIndex = this._buffer.indexOf('\n\n')) !== -1) {
      const frame = this._buffer.slice(0, boundaryIndex);
      this._buffer = this._buffer.slice(boundaryIndex + 2);

      const parsed = this._parseFrame(frame);
      if (parsed) {
        events.push(parsed);
      }
    }

    return events;
  }

  /**
   * Flush any remaining buffered data.
   * Attempts to parse incomplete frames (e.g., missing trailing \n\n).
   *
   * @returns {Array<Object>} Parsed event objects from remaining buffer
   */
  flush() {
    if (!this._buffer.trim()) {
      this._buffer = '';
      return [];
    }

    const parsed = this._parseFrame(this._buffer);
    this._buffer = '';

    if (parsed) {
      return [parsed];
    }

    console.warn('SSEFrameParser: Discarding incomplete frame on flush');
    return [];
  }

  /**
   * Reset the parser, discarding any buffered data.
   */
  reset() {
    this._buffer = '';
  }

  /**
   * Parse a single SSE frame string into an event object.
   *
   * @param {string} frame - Raw frame string (without trailing \n\n)
   * @returns {Object|null} Parsed event object, or null if invalid
   * @private
   */
  _parseFrame(frame) {
    const trimmed = frame.trim();
    if (!trimmed) return null;

    // Strip "data: " or "data:" prefix
    let jsonStr;
    if (trimmed.startsWith('data: ')) {
      jsonStr = trimmed.slice(6);
    } else if (trimmed.startsWith('data:')) {
      jsonStr = trimmed.slice(5);
    } else {
      // Not an SSE data frame — skip
      return null;
    }

    try {
      return JSON.parse(jsonStr);
    } catch {
      console.warn('SSEFrameParser: Malformed JSON in SSE frame:', jsonStr.slice(0, 100));
      return null;
    }
  }
}

export default SSEFrameParser;
